// Import the functions you need from the SDKs you need
import { getAnalytics } from "firebase/analytics";
import { initializeApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";
import { getAuth, signInAnonymously, signInWithCustomToken } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, getFirestore, query, setDoc, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getMessaging, getToken } from "firebase/messaging";
import { ToLocaleDateStringISO } from "./components/utils";
import config from "./config.json";
import { Booking } from "./types/Booking.type";

    // Handle incoming messages. Called when:
// - a message is received while the app has focus
// - the user clicks on an app notification created by a service worker
//   `messaging.onBackgroundMessage` handler.
import { onMessage } from "firebase/messaging";
import { User } from "./types/User.type";

console.log(`config environment: ${config.REACT_APP_ENVIRONMENT}`)

// config.release.json overrides the config file for the "production" environment
// config.dev.json overrides the config file for "development" environment
// See the deployment pipelines for this transformation
const firebaseConfig = {
  apiKey: config.REACT_APP_apiKey,
  authDomain: config.REACT_APP_authDomain,
  projectId: config.REACT_APP_projectId,
  storageBucket: config.REACT_APP_storageBucket,
  messagingSenderId: config.REACT_APP_messagingSenderId,
  appId: config.REACT_APP_appId,
  measurementId: config.REACT_APP_measurementId
};

const UrlFirebaseConfig = new URLSearchParams(
  firebaseConfig.toString()
);

const swUrl = `http://localhost:3000/firebase-messaging-sw.js?${UrlFirebaseConfig}`;

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Only initialize App Check in deployed areas (ReCAPTCHA fails on localhost)
if (window.location.hostname !== "localhost") {
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(config.CAPTCHA_SITE_KEY),
    isTokenAutoRefreshEnabled: true
  });
}
const auth = getAuth(app);
const functions = getFunctions(app);

/**
 * Authenticate to Firebase using a verified MSAL ID token.
 * Call this after MSAL login succeeds. It calls the mintCustomToken Cloud Function
 * which verifies the MSAL token and returns a Firebase custom token.
 * This replaces the anonymous auth with a real identity.
 */
export const signInWithMsal = async (msalIdToken: string): Promise<void> => {
  try {
    const mintCustomToken = httpsCallable(functions, 'mintCustomToken');
    const result = await mintCustomToken({ msalToken: msalIdToken });
    const { token } = result.data as { token: string };
    await signInWithCustomToken(auth, token);
    console.log("Firebase: signed in with custom token (MSAL-verified)");
  } catch (error: any) {
    console.warn("Firebase custom token auth failed, falling back to anonymous:", error.message);
    // Only fall back to anonymous if no authenticated user exists yet
    if (!auth.currentUser || auth.currentUser.isAnonymous) {
      await signInAnonymously(auth);
      console.log("Firebase: signed in anonymously (fallback)");
    }
  }
};
getAnalytics(app)
export const db = getFirestore(app);
export const messaging = getMessaging(app);

// Recieve FCM messages

onMessage(messaging, (payload) => {
    try {
      console.log('Received foreground message: ', payload);
      const notificationTitle = payload.data?.title as string;
      const notificationOptions = { body: payload.data?.body as string };
      const notification = new Notification(notificationTitle, notificationOptions);
      // self.registration.showNotification(notificationTitle, notificationOptions);
    } catch (err) {
      console.log(err);
    }
  });

// Shared firebase constants
export const BOOKINGS = "bookings"
export const WAITLIST = "waitlist"
export const USER = "user"

// Shared firebase queries 
export const fetchBookingsFiltered = async (): Promise<Booking[]> => {
  const filteredBookingsQuery = query(collection(db, BOOKINGS), where('date', '>=', ToLocaleDateStringISO(new Date())));
  return await getDocs(filteredBookingsQuery).then(querySnapshot => {
    if (!querySnapshot.empty) {
      const bookings = [] as Booking[]
      querySnapshot.docs.forEach(doc => {
        bookings.push(doc.data() as Booking)
      })
      return bookings
    } else {
      console.warn("Bookings are empty")
      return []
    }
  });
}


/// SW STUFF

export const getOrRegisterServiceWorker = () => {
  if (
    "serviceWorker" in navigator &&
    typeof window.navigator.serviceWorker !== "undefined"
  ) {
    return window.navigator.serviceWorker
      .getRegistration("/firebase-push-notification-scope")
      .then((serviceWorker) => {
        if (serviceWorker) return serviceWorker;
        return window.navigator.serviceWorker.register(
          "/firebase-messaging-sw.js",
          {
            scope: "/firebase-push-notification-scope",
          }
        );
      });
  }
  throw new Error("The browser doesn`t support service worker.");
};

export const getFirebaseToken = async () => {
  try {
    if (messaging) {
      return getOrRegisterServiceWorker().then((serviceWorkerRegistration) => {
        return Promise.resolve(
          getToken(messaging, {
            vapidKey: config.REACT_APP_VAPIDKEY,
            serviceWorkerRegistration,
          })
        );
      });
    }
  } catch (error) {
    console.log("An error occurred while retrieving token. ", error);
  }
};

export const saveFirebaseToken = async (userPrincipalName: string | undefined) => {
  if (!userPrincipalName)
    return;
  getFirebaseToken().then(async (currentToken: string | undefined) => {
    if (currentToken) {
      const docRef = doc(db, USER, userPrincipalName.toLowerCase() as string)
      const user = (await getDoc(docRef)).data() as User
      // We use a set to avoid duplicates
      const notificationTokens = new Set(user.notificationTokens ?? [])
      notificationTokens.add(currentToken)
      console.log(`User notification tokens: ${[...notificationTokens]}`);
      const updateObject = { notificationTokens: [...notificationTokens] }
      await setDoc(docRef, updateObject, {merge: true})
        .then(async () => {
          console.log("Successfully set the token");
        })
        .catch((error) => {
          console.log(`Unsuccessful token setting - ERR: ${error}`)
        })
    } else {
        console.log('No registration token available. Request permission to generate one.');
    }
  });
}
